import type { FilterJourneyIndex } from '@/src/types/srpJourneyIndex'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useActiveFilterDataIndex from '../useActiveFilterDataIndex'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

export interface LayoverAirportOption {
    id: string
    label: string
    isSelected: boolean
}

const useLayoverAirportFilterManager = (filterJourneyIndex: FilterJourneyIndex = 0) => {
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()
    const updateCache = useUpdateSearchResultCache()
    const { filterData, jsons, isMulticity } = useSearchResults()
    const { filterDataIndex } = useActiveFilterDataIndex(filterJourneyIndex)
    const targetLayoverAirport = filterData?.layover_Airport?.[filterDataIndex]
    const selectedOptions = targetLayoverAirport?.selectedOptions || []
    const selectedOptionSet = useMemo(() => new Set(selectedOptions.map(value => value?.toUpperCase())), [selectedOptions])
    const layoverValues = targetLayoverAirport?.values || []
    const airportsJson = jsons?.ap || {}
    const title = t('layoverAirport')

    const layoverAirportOptions = useMemo(() => {
        return layoverValues
            .map(value => {
                const airportCode = value?.toUpperCase()
                const airport = airportsJson[airportCode]
                const label = airport ? `${airport.n}, ${airport.c}` : airportCode
                return {
                    id: airportCode,
                    label,
                    isSelected: selectedOptionSet.has(airportCode)
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }, [airportsJson, layoverValues, selectedOptionSet])

    const handleSelectToggle = useCallback(
        (id: string) => {
            const isRemoving = selectedOptions.includes(id)
            const selectedValues = isRemoving
                ? selectedOptions.filter(value => value !== id)
                : [...selectedOptions, id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'layover_airport',
                action_value: selectedValues.join(',') || 'none'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.layover_Airport?.[filterDataIndex]) {
                    currentData.filterData.layover_Airport[filterDataIndex].selectedOptions = selectedValues
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [selectedOptions, filterDataIndex, triggerSrpEvent, updateCache]
    )

    const handleReset = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'layover_airport'
        })

        updateCache(currentData => {
            if (currentData?.filterData?.layover_Airport?.[filterDataIndex]) {
                currentData.filterData.layover_Airport[filterDataIndex].selectedOptions = []
            }
            return currentData as NonNullable<typeof currentData>
        })
    }, [filterDataIndex, triggerSrpEvent, updateCache])

    const shouldShowReset = useMemo(() => {
        return selectedOptions.length > 0
    }, [selectedOptions])

    const shouldShowResetForAllJourneys = useMemo(() => {
        if (!isMulticity) return false
        return !!filterData?.layover_Airport?.some(airport => airport.selectedOptions?.length)
    }, [isMulticity, filterData])

    const handleResetAllJourneys = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'layover_airport'
        })

        updateCache(currentData => {
            currentData?.filterData?.layover_Airport?.forEach(airport => {
                airport.selectedOptions = []
            })
            return currentData as NonNullable<typeof currentData>
        })
    }, [triggerSrpEvent, updateCache])

    return {
        title,
        layoverAirportOptions,
        handleSelectToggle,
        handleReset,
        handleResetAllJourneys,
        shouldShowReset,
        shouldShowResetForAllJourneys,
        isMulticity
    }
}

export default useLayoverAirportFilterManager
